import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Sidebar from "../Sidebar/Sidebar";
import { viewAllAdmins, updateAdminStatus } from "../Apis/SuperadminApis";
import {fetchTeamRequests} from "../Apis/AdminApis";
import "./CompanyDetail.css";
import pavamanLogo from "../images/drone.png";
import PopupMessage from "../PopupMessage/PopupMessage";

const CompanyDetail = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [companyPilots, setCompanyPilots] = useState([]);
  const [companyAdmins, setCompanyAdmins] = useState([]);
  const [companyActivity, setCompanyActivity] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupType, setPopupType] = useState("info");
  const [pilotsPage, setPilotsPage] = useState(1);
  const [pilotsPerPage] = useState(10);
  const [pilotsSearchTerm, setPilotsSearchTerm] = useState("");

  useEffect(() => {
    loadCompanyDetails();
  }, [companyId]);

  const loadCompanyDetails = async () => {
    try {
      setIsLoading(true);
      const adminsRes = await viewAllAdmins({});
      if (adminsRes?.error?.includes("Unauthorized")) {
  setPopupMessage("Session expired, redirecting to login page");
  setPopupType("error");
  setPopupOpen(true);

  setTimeout(() => {
    navigate("/login");
  }, 2000);

  return;
}
      const companyData = adminsRes?.data?.find(a => a.id === parseInt(companyId));
      
      if (!companyData) {
        setPopupMessage("Company not found");
        setPopupType("error");
        setPopupOpen(true);
        setTimeout(() => navigate("/superadmin/dashboard"), 2000);
        return;
      }
      
      setCompany(companyData);
      const pilotsRes = await fetchTeamRequests({ admin_id: companyData.id });
      setCompanyPilots(pilotsRes?.pilots || []);
      setCompanyAdmins([companyData]);
      setCompanyActivity([
        { action: "Company created", date: companyData.created_on, user: "System" },
        { action: "Admin registered", date: companyData.created_on, user: companyData.email }
      ]);
      
 } catch (err) {

  if (
    err?.response?.status === 401 ||
    err?.response?.data?.error?.includes("Unauthorized")
  ) {
    setPopupMessage("Session expired, redirecting to login page");
    setPopupType("error");
    setPopupOpen(true);

    setTimeout(() => {
      navigate("/login");
    }, 2000);

    return;
  }

  console.error("Failed to load company details:", err);

  setPopupMessage("Failed to load company details");
  setPopupType("error");
  setPopupOpen(true);
} finally {
      setIsLoading(false);
    }
  };

const handleUpdateStatus = async (action) => {
  try {
    setIsUpdating(true);

    const response = await updateAdminStatus({
      email: company.email,
      action: action
    });

    // Session expired handling
    if (response?.error?.includes("Unauthorized")) {
      setPopupMessage("Session expired, redirecting to login page");
      setPopupType("error");
      setPopupOpen(true);

      setTimeout(() => {
        navigate("/login");
      }, 2000);

      return;
    }

    if (response.error) {
      setPopupMessage(response.error);
      setPopupType("error");
      setPopupOpen(true);
    } else if (response.message) {
      setPopupMessage(response.message);
      setPopupType("success");
      setPopupOpen(true);

      await loadCompanyDetails();
    }

  } catch (err) {

    // Session expired handling
    if (
      err?.response?.status === 401 ||
      err?.response?.data?.error?.includes("Unauthorized")
    ) {
      setPopupMessage("Session expired, redirecting to login page");
      setPopupType("error");
      setPopupOpen(true);

      setTimeout(() => {
        navigate("/login");
      }, 2000);

      return;
    }

    console.error("Failed to update status:", err);

    setPopupMessage("Failed to update company status");
    setPopupType("error");
    setPopupOpen(true);

  } finally {
    setIsUpdating(false);
    setShowActionModal(false);
    setActionType(null);
  }
};

  const openActionModal = (type) => {
    setActionType(type);
    setShowActionModal(true);
  };

  const closeActionModal = () => {
    setShowActionModal(false);
    setActionType(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const getStatusBadge = (approval, status) => {
    if (approval === 1) {
      return { text: 'Pending', color: '#f39c12', bg: '#fef3c7', icon: '⏳' };
    } else if (approval === 2 && status === 1) {
      return { text: 'Active', color: '#00B894', bg: '#d1fae5', icon: '✓' };
    } else if (approval === 3) {
      return { text: 'Suspended', color: '#e74c3c', bg: '#fee2e2', icon: '✗' };
    }
    return { text: 'Inactive', color: '#95a5a6', bg: '#f3f4f6', icon: '○' };
  };

  const filteredPilots = companyPilots.filter(pilot => 
    pilot.name?.toLowerCase().includes(pilotsSearchTerm.toLowerCase()) ||
    pilot.email?.toLowerCase().includes(pilotsSearchTerm.toLowerCase())
  );

  const indexOfLastPilot = pilotsPage * pilotsPerPage;
  const indexOfFirstPilot = indexOfLastPilot - pilotsPerPage;
  const currentPilots = filteredPilots.slice(indexOfFirstPilot, indexOfLastPilot);
  const totalPages = Math.ceil(filteredPilots.length / pilotsPerPage);

  if (isLoading) {
    return (
      <div className="dashboard-wrapper">
        <Sidebar />
        <div className="dashboard-content">
          <div className="custom-loading-container">
            <img src={pavamanLogo} alt="Loading" className="spinning-logo" />
            <p className="loading-text">Loading company details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!company) return null;

  const status = getStatusBadge(company.approval, company.status);

  return (
    <div className="dashboard-wrapper">
      <Sidebar />
      
      <div className="dashboard-content">
        <div className="company-detail-header">
          <button className="back-btn" onClick={() => navigate("/superadmin/dashboard")}>
            ← Back to Dashboard
          </button>
          <div className="company-header-info">
            {company.logo_url ? (
              <img src={company.logo_url} alt={company.name} className="company-header-logo" />
            ) : (
              <div className="company-header-logo-placeholder">🏢</div>
            )}
            <div>
              <h1 className="company-title">{company.name || "Company Name"}</h1>
              <p className="company-subtitle">{company.email}</p>
            </div>
          </div>
        </div>

        <div className="company-info-grid">
          <div className="company-profile-card">
            <h3>Company Profile</h3>
            <div className="company-profile-content">
              <div className="profile-row">
                <span className="profile-label">Organization Name:</span>
                <span className="profile-value">{company.name || "N/A"}</span>
              </div>
              <div className="profile-row">
                <span className="profile-label">Contact Person:</span>
                <span className="profile-value">{company.contact_name || "N/A"}</span>
              </div>
              <div className="profile-row">
                <span className="profile-label">Email:</span>
                <span className="profile-value">{company.email || "N/A"}</span>
              </div>
              <div className="profile-row">
                <span className="profile-label">Mobile Number:</span>
                <span className="profile-value">{company.mobile_no || "N/A"}</span>
              </div>
              <div className="profile-row">
                <span className="profile-label">Address:</span>
                <span className="profile-value">{company.address || "N/A"}</span>
              </div>
              <div className="profile-row">
                <span className="profile-label">Status:</span>
                <span className="profile-value">
                  <span className="sa-status-badge" style={{ background: status.bg, color: status.color }}>
                    {status.icon} {status.text}
                  </span>
                </span>
              </div>
              {company.gst_url && (
                <div className="profile-row">
                  <span className="profile-label">GST Certificate:</span>
                  <a href={company.gst_url} target="_blank" rel="noopener noreferrer" className="gst-link">
                    View Certificate 📄
                  </a>
                </div>
              )}
            </div>
          </div>
<div className="company-actions-card">
  <h3>Account Management</h3>

  <div className="action-buttons-container">
    {company.approval === 2 && company.status === 1 && (
      <button
        className="action-btn-suspend"
        onClick={() => openActionModal("suspend")}
      >
        🔒 Reject Account
      </button>
    )}

    {company.approval === 3 && (
      <button
        className="action-btn-activate"
        onClick={() => openActionModal("activate")}
      >
        🔄 Reactivate Account
      </button>
    )}

    {company.approval === 1 && (
      <>
        <button
          className="action-btn-approve"
          onClick={() => openActionModal("approve")}
        >
          ✓ Approve Account
        </button>

        <button
          className="action-btn-reject"
          onClick={() => openActionModal("reject")}
        >
          ✗ Reject Account
        </button>
      </>
    )}
  </div>

  <div className="activity-section">
    <h3>Recent Activity</h3>

    {companyActivity.length > 0 ? (
      <div className="activity-timeline">
        {companyActivity.map((activity, idx) => (
          <div key={idx} className="activity-item">
            <div className="activity-dot"></div>

            <div className="activity-content">
              <div className="activity-action">{activity.action}</div>
              <div className="activity-date">
                {formatDateTime(activity.date)}
              </div>
              <div className="activity-user">
                by {activity.user}
              </div>
            </div>
          </div>
        ))}
      </div>
    ) : (
      <p className="no-data-message">No recent activity</p>
    )}
  </div>
</div>
        </div>
      </div>

      {showActionModal && (
        <div className="sa-modal-overlay" onClick={closeActionModal}>
          <div className="sa-modal-content sa-modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="sa-modal-header">
              <h2>
                {actionType === 'approve' && 'Approve Company'}
                {actionType === 'reject' && 'Reject Company'}
                {actionType === 'suspend' && 'Suspend Company'}
                {actionType === 'activate' && 'Activate Company'}
              </h2>
              <button className="sa-modal-close" onClick={closeActionModal}>×</button>
            </div>
            <div className="sa-modal-body">
              <div className="sa-confirm-container">
                <div className={`sa-confirm-icon sa-confirm-${actionType}`}>
                  {actionType === 'approve' && '✓'}
                  {actionType === 'reject' && '✗'}
                  {actionType === 'suspend' && '🔒'}
                  {actionType === 'activate' && '🔄'}
                </div>
                <p className="sa-confirm-text">
                  Are you sure you want to {actionType} <strong>{company.name || company.email}</strong>?
                </p>
                <p className="sa-confirm-subtext">
                  {actionType === 'approve' && 'The company will be able to access the platform and manage their fleet.'}
                  {actionType === 'reject' && 'The company registration will be rejected.'}
                  {actionType === 'suspend' && 'The company account will be suspended. They will not be able to access the platform.'}
                  {actionType === 'activate' && 'The company account will be activated. They will be able to access the platform again.'}
                </p>
                <div className="sa-confirm-buttons">
                  <button className="sa-confirm-cancel" onClick={closeActionModal} disabled={isUpdating}>
                    Cancel
                  </button>
                  <button 
                    className={`sa-confirm-${actionType}`}
                    onClick={() => handleUpdateStatus(actionType)}
                    disabled={isUpdating}
                  >
                    {isUpdating ? (
                      <>
                        <span className="sa-spinner-small"></span>
                        Processing...
                      </>
                    ) : (
                      `Yes, ${actionType.charAt(0).toUpperCase() + actionType.slice(1)}`
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <PopupMessage
        isOpen={popupOpen}
        onClose={() => setPopupOpen(false)}
        message={popupMessage}
        type={popupType}
      />
    </div>
  );
};

export default CompanyDetail;